/**
 * Query key factory for flow queries.
 * Provides consistent, hierarchical keys for cache management.
 */
export const flowQueryKeys = {
  all: ['flows'] as const,
  lists: () => [...flowQueryKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) =>
    [...flowQueryKeys.lists(), filters] as const,
  details: () => [...flowQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...flowQueryKeys.details(), id] as const,
  /**
   * Nested under `all` on purpose: the broad
   * `invalidateQueries({ queryKey: flowQueryKeys.all })` that
   * follows a content edit is exactly what can make a doc's
   * pdf stale, so it has to refresh this entry too.
   */
  preparation: (flowId: string) =>
    [...flowQueryKeys.all, 'preparation', flowId] as const,
  /**
   * Prefix for every Document Engine collaboration-config entry.
   * Invalidate with this after merge/delete so JWTs scoped to a
   * removed `storageDocumentId` cannot be reused (PORTAL-1A7).
   */
  collectionConfigs: () => [...flowQueryKeys.all, 'collection-config'] as const,
  /**
   * `storageDocumentId` is part of the key on purpose: template
   * collection ids are stable across delete+re-merge, but Nutrient
   * mints a new document id each copy. Omitting it lets a stale JWT
   * for the deleted document win for up to `COLLABORATION_STALE_TIME`
   * and surface as Document Engine "Document not found".
   */
  collectionConfig: (
    flowId: string,
    collectionId: string,
    configType: string,
    storageDocumentId = ''
  ) =>
    [
      ...flowQueryKeys.collectionConfigs(),
      configType,
      flowId,
      collectionId,
      storageDocumentId,
    ] as const,
} as const;
